import React, { useEffect, useState } from 'react';
import { colors, css, fonts} from '../../Styles';
import { progressColor } from '../../Definitions';
import { progressTextColor } from '../../Definitions/progressColor';
import { TooltipString } from '..';
import { useTranslation } from 'react-i18next';

export enum ProgressTypes {
  consumed = "consumed",
  remaining = "remaining",
}

export enum BarTypes {
  primary = "primary",
  pen = "pen",
}

interface ProgressBar_Props {
  total?: number
  progressed?: number
  progressType?: ProgressTypes
  barType?: BarTypes
  animation?: boolean
  labels?: {
    header?: string
    penLabel?: string | number
    unit?: JSX.Element
    noDataDisclaimer?: string
  }
  extension?: {
    hasExtension?: boolean
    extensionOnly?: boolean
  }
}

export default function ProgressBar ({
  total = 0,
  progressed = 0,
  progressType = ProgressTypes.remaining,
  barType = BarTypes.primary,
  animation = false,
  labels,
  extension
}: ProgressBar_Props) {
  const { t } = useTranslation();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setAnimate(true);
  }, []);

  if (progressed === 0 && total === 0 && !labels?.penLabel) {
    return <p style={{ ...css.emptyPen, opacity: "0.5" }}>
      { labels?.noDataDisclaimer ? labels.noDataDisclaimer : t("noData", {defaultValue: "No Data"})}
    </p>
  }

  // === LOGIK ===
  const isOverConsumed = progressType === ProgressTypes.consumed && progressed > total;
  const isOverRemaining = progressType === ProgressTypes.remaining && progressed < 0;
  const tooMuchFed = isOverConsumed || isOverRemaining;

  // procent: clamp til 100% ved consumed
  const progressedPercentage =
    total > 0
      ? progressType === ProgressTypes.consumed
        ? Math.min((progressed / total) * 100, 100)
        : !tooMuchFed
          ? (progressed / total) * 100
          : 0
      : 0;

  // tekst til bar
  const displayedProgress = progressType === ProgressTypes.consumed
    ? isOverConsumed
      ? total
      : progressed
    : !tooMuchFed
      ? progressed
      : 0;

  // extension værdi afrundet til 2 decimaler
  const extensionValue = (() => {
    let val = 0;
    if (progressType === ProgressTypes.consumed && isOverConsumed) {
      val = progressed - total;
    } else if (progressType === ProgressTypes.remaining && isOverRemaining) {
      val = Math.abs(progressed);
    }
    return Math.round(val * 100) / 100; // 2 decimaler
  })();

  const _width = animation 
    ? animate 
      ? `${progressedPercentage}%` 
      : "0%"
    : `${progressedPercentage}%`;

  // === RENDER ===
  const progressBar = (
    <div style={{
      width: barType === BarTypes.primary ? "100%" : "80px",
      maxWidth: "100px",
      backgroundColor: barType === BarTypes.primary ? colors.greyTertiary : colors.greyLightSecondary,
      height: barType === BarTypes.primary ? "15px" : "30px",
      position: "relative",
      borderRadius: barType === BarTypes.primary ? "10px" : "0px",
      overflow: "hidden",
      pointerEvents: "none"
    }}>
      <div style={{
        backgroundColor: progressColor(progressedPercentage),
        height: "100%",
        width: _width,
        position: "absolute",
        left: "0px",
        transition: "width 1s ease"
      }}/>
      { barType === BarTypes.primary
        ? <div style={{position: "absolute", display: "grid", gridTemplateColumns: "5fr 1fr 5fr", width: "100%", height: "100%"}}>
            <p style={{ ...css.progressBarLabel, justifyContent: "end" }}>{displayedProgress}</p>
            <p style={{ ...css.progressBarLabel, justifyContent: "center" }}>/</p>
            <p style={{ ...css.progressBarLabel, justifyContent: "start" }}>{total}</p>
          </div>
        : <p style={{ ...css.progressBarText, position: "absolute", transform: "translateX(-50%)", left: "50%", color: progressTextColor(progressedPercentage) }}>
            {labels?.penLabel}
          </p>
      }
    </div>
  );

  const negative = extensionValue !== 0 && (
    <TooltipString
      overrides={{ contentstring: { color: colors.systemRed, ...fonts.fontSize5,pointerEvents: "none" } }}
      contentString={
        <div style={{ border: "1px solid " + colors.systemRed, borderRadius: "10px", width: "50px", height: "15px", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: "0" }}>
          {`+${Math.abs(progressed)}`}
        </div>
      }
      // tooltipString={tooMuchFed && extension?.hasExtension ? undefined : `Kg fed more than expected`}
    />
  );

  return (
    labels?.header ? (
      <div style={{height: "65px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", gap: "9px"}}>
        <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, textAlign: "center"}}>
          {labels.header}
        </p>
        {progressBar}
        {labels.unit}
      </div>
    ) : (
      extension?.extensionOnly
        ? (tooMuchFed ? negative : null)
        : (tooMuchFed && extension?.hasExtension
          ? <div style={{ display: "flex", alignItems: "center", gap: "5px", zIndex: "99", position: "relative" }}>
              {progressBar}{negative}
            </div>
          : progressBar)
    )
  );
}
